import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import edit from '~/assets/icons/edit.svg';
import flag from '~/assets/icons/flag.svg';
import input from '~/assets/icons/input.svg';
import tryIcon from '~/assets/icons/try.svg';
import { Button, ButtonColors, ButtonHeight } from '~/components/ui/Button';
import { SectionHeading } from '~/components/ui/SectionHeading';
import { Text } from '~/components/ui/Text';
import { TextInputAlign } from '~/components/ui/TextInput';
import { rage, useInputAction } from '~/rage';
import { InputAction, WalletType } from '~/shared';
import { useBuy } from '../../hooks';
import {
	setClassicNumberInput,
	setCustomNumber,
	setCustomNumberInput,
	setNumber,
	setNumberInput,
	setPlateInputFocus,
	setRegionCode,
	setRegionSelectVisible,
	setSelectedTypeIndex,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux/';
import { isCustomType, validateNumberPlate } from '../../utils';
import { InputWithIcon } from '../InputWithIcon';
import { PlatePreview } from '../PlatePreview';
import { plateTypeList } from './ChangeTab.model';
import {
	StyledBottomGrid,
	StyledButtonContent,
	StyledContainer,
	StyledPlateContainer,
	StyledRegionContainer,
	StyledRegionSelectButtonContent,
} from './ChangeTab.styled';

const ChangeTab: React.FC = () => {
	const dispatch = useLayoutDispatch();

	const number = useLayoutSelector((state) => state.plateBuying.number);
	const regionCode = useLayoutSelector((state) => state.plateBuying.regionCode);
	const numberInput = useLayoutSelector(
		(state) => state.plateBuying.numberInput,
	);
	const customNumber = useLayoutSelector(
		(state) => state.plateBuying.customNumber,
	);
	const classicNumberInput = useLayoutSelector(
		(state) => state.plateBuying.classicNumberInput,
	);
	const customNumberInput = useLayoutSelector(
		(state) => state.plateBuying.customNumberInput,
	);
	const selectedTypeIndex = useLayoutSelector(
		(state) => state.plateBuying.selectedTypeIndex,
	);
	const regionState = useLayoutSelector((state) => state.plateBuying.region);
	const plateInputFocus = useLayoutSelector(
		(state) => state.plateBuying.plateInputFocus,
	);

	const [repeatedLetters, setRepeatedLetters] = useState<boolean>(false);
	const [repeatedNumbers, setRepeatedNumbers] = useState<boolean>(false);
	const [regionName, setRegionName] = useState<string>('');
	const { handleBuy } = useBuy();

	const tryDisabled = useMemo(() => {
		const selectedType = plateTypeList[selectedTypeIndex];
		if (isCustomType(selectedType.type)) {
			return !customNumber || customNumber.length === 0;
		}
		return !validateNumberPlate(selectedType.type, number, regionCode);
	}, [number, regionCode, customNumber, selectedTypeIndex]);

	const handlePreview = useCallback(() => {
		const selectedType = plateTypeList[selectedTypeIndex];

		const selectedNumber = isCustomType(selectedType.type)
			? customNumber
			: number;
		const selectedRegion = isCustomType(selectedType.type) ? '' : regionCode;

		if (!isCustomType(selectedType.type)) {
			if (
				!validateNumberPlate(selectedType.type, selectedNumber, selectedRegion)
			) {
				rage.notifications.showError('Номер не соответствует формату');
				return;
			}

			if (!selectedRegion) {
				rage.notifications.showError('Введите корректный регион');
				return;
			}
		}

		if (!regionName && !isCustomType(selectedType.type)) {
			rage.notifications.showError('Введите корректный регион');
			return;
		}

		if (selectedNumber && (selectedRegion || isCustomType(selectedType.type))) {
			const selectedType = plateTypeList[selectedTypeIndex];
			rage.vehicleNumberPlates
				.previewNumberPlate({
					type: selectedType.type,
					text: selectedNumber,
					region: selectedRegion,
				})
				.catch((err) => rage.notifications.showException(err));
		}
	}, [
		number,
		regionCode,
		customNumber,
		selectedTypeIndex,
		regionName,
		validateNumberPlate,
	]);

	const sanitizeCustomInput = useCallback((raw: string) => {
		return raw
			.toUpperCase()
			.replace(/[^A-Z0-9]/g, '')
			.slice(0, 8);
	}, []);

	const sanitizeClassicInput = useCallback((raw: string) => {
		let input = raw.toUpperCase();
		const rusToEng: Record<string, string> = {
			А: 'A',
			В: 'B',
			Е: 'E',
			К: 'K',
			М: 'M',
			Н: 'H',
			О: 'O',
			Р: 'P',
			С: 'C',
			Т: 'T',
			У: 'Y',
			Х: 'X',
		};
		input = input
			.split('')
			.map((char) => rusToEng[char] || char)
			.join('');

		const validLetters = 'ABEKMHOPCTYX';
		const allowed = 'ABEKMHOPCTYX0123456789';
		input = input
			.split('')
			.filter((c) => allowed.includes(c))
			.join('');

		let stage = 0;
		let out = '';
		for (let i = 0; i < input.length && stage < 6; i++) {
			const ch = input[i];
			if (stage === 0) {
				if (validLetters.includes(ch)) {
					out += ch;
					stage = 1;
				}
				continue;
			}
			if (stage >= 1 && stage <= 3) {
				if (/\d/.test(ch)) {
					out += ch;
					stage += 1;
				}
				continue;
			}
			if (stage === 4 || stage === 5) {
				if (validLetters.includes(ch)) {
					out += ch;
					stage += 1;
				}
				continue;
			}
		}

		return out;
	}, []);

	const handleNumberInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const selectedType = plateTypeList[selectedTypeIndex];
			const raw = e.target.value;
			if (isCustomType(selectedType.type)) {
				dispatch(setNumberInput(sanitizeCustomInput(raw)));
			} else {
				dispatch(setNumberInput(sanitizeClassicInput(raw)));
			}
		},
		[selectedTypeIndex, sanitizeClassicInput, sanitizeCustomInput, dispatch],
	);

	const changeType = (newIndex: number) => {
		const currentType = plateTypeList[selectedTypeIndex].type;
		const newType = plateTypeList[newIndex].type;
		const currentIsCustom = isCustomType(currentType);
		const newIsCustom = isCustomType(newType);

		if (currentIsCustom !== newIsCustom) {
			// Переходим между именными и неименными — сохраняем текущий ввод
			// в свою категорию и восстанавливаем ранее введённый для новой.
			if (currentIsCustom) {
				dispatch(setCustomNumberInput(numberInput));
				dispatch(setNumberInput(sanitizeClassicInput(classicNumberInput)));
			} else {
				dispatch(setClassicNumberInput(numberInput));
				dispatch(setNumberInput(sanitizeCustomInput(customNumberInput)));
			}
		} else if (newIsCustom) {
			dispatch(setNumberInput(sanitizeCustomInput(numberInput)));
		} else {
			dispatch(setNumberInput(sanitizeClassicInput(numberInput)));
		}

		dispatch(setSelectedTypeIndex(newIndex));
	};

	const handlePrevType = () => {
		const newIndex =
			selectedTypeIndex === 0
				? plateTypeList.length - 1
				: selectedTypeIndex - 1;

		changeType(newIndex);
	};

	const handleNextType = () => {
		const newIndex =
			selectedTypeIndex === plateTypeList.length - 1
				? 0
				: selectedTypeIndex + 1;

		changeType(newIndex);
	};

	useEffect(() => {
		if (isCustomType(plateTypeList[selectedTypeIndex].type)) {
			setRepeatedNumbers(false);
			setRepeatedLetters(false);
			return;
		}

		if (number.length !== 6) {
			setRepeatedNumbers(false);
			setRepeatedLetters(false);
			return;
		}

		if (
			number[1] === number[2] &&
			number[2] === number[3] &&
			number[1] !== ' '
		) {
			setRepeatedNumbers(true);
		} else {
			setRepeatedNumbers(false);
		}

		if (
			number[0] === number[4] &&
			number[4] === number[5] &&
			number[0] !== ' '
		) {
			setRepeatedLetters(true);
		} else {
			setRepeatedLetters(false);
		}
	}, [number, selectedTypeIndex]);

	useEffect(() => {
		if (isCustomType(plateTypeList[selectedTypeIndex].type)) {
			setRegionName('');
			return;
		}

		setRegionName(
			rage.vehicleNumberPlates.getRegionNameByCode(regionCode) || '',
		);
	}, [regionCode, selectedTypeIndex]);

	useEffect(() => {
		if (regionState && regionState.code.toString() !== regionCode) {
			const newRegionCode = regionState.code
				.toString()
				.padStart(2, '0')
				.slice(-3);
			dispatch(setRegionCode(newRegionCode));
		}
	}, [regionState, regionCode, dispatch]);

	useEffect(() => {
		const selectedType = plateTypeList[selectedTypeIndex];
		if (isCustomType(selectedType.type)) {
			dispatch(setCustomNumber(sanitizeCustomInput(numberInput)));
		} else {
			if (!numberInput) {
				dispatch(setNumber(''));
				return;
			}

			let input = numberInput.toUpperCase().trim();
			const rusToEng: Record<string, string> = {
				А: 'A',
				В: 'B',
				Е: 'E',
				К: 'K',
				М: 'M',
				Н: 'H',
				О: 'O',
				Р: 'P',
				С: 'C',
				Т: 'T',
				У: 'Y',
				Х: 'X',
			};
			input = input
				.split('')
				.map((char) => rusToEng[char] || char)
				.join('');
			input = input.replace(/[^ABEKMHOPCTYX0-9]/g, '');

			const validLetters = 'ABEKMHOPCTYX';
			const slots = [' ', ' ', ' ', ' ', ' ', ' '];

			let stage = 0;
			for (let i = 0; i < input.length && stage < 6; i++) {
				const ch = input[i];
				if (stage === 0) {
					if (validLetters.includes(ch)) {
						slots[0] = ch;
						stage = 1;
					}
					continue;
				}
				if (stage >= 1 && stage <= 3) {
					if (/\d/.test(ch)) {
						slots[stage] = ch;
						stage += 1;
					}
					continue;
				}
				if (stage === 4 || stage === 5) {
					if (validLetters.includes(ch)) {
						slots[stage] = ch;
						stage += 1;
					}
					continue;
				}
			}

			dispatch(setNumber(slots.join('')));
		}
	}, [numberInput, selectedTypeIndex, sanitizeCustomInput, dispatch]);

	useInputAction(InputAction.CommonMenuNavLeft, () => {
		if (plateInputFocus) {
			return;
		}

		handlePrevType();
	});
	useInputAction(InputAction.CommonMenuNavRight, () => {
		if (plateInputFocus) {
			return;
		}

		handleNextType();
	});
	useInputAction(InputAction.CommonMenuAccept, () =>
		handleBuy(WalletType.Cash),
	);

	return (
		<StyledContainer>
			<StyledRegionContainer>
				<SectionHeading text={'Редактор номера'} icon={edit} />
			</StyledRegionContainer>
			<StyledPlateContainer>
				<PlatePreview
					plateType={plateTypeList[selectedTypeIndex]}
					regionLabel={regionName}
					handlePrevType={handlePrevType}
					handleNextType={handleNextType}
					number={
						isCustomType(plateTypeList[selectedTypeIndex].type)
							? customNumber
							: number
					}
					region={regionCode}
					repeatedLetters={repeatedLetters}
					repeatedNumbers={repeatedNumbers}
				/>
			</StyledPlateContainer>
			<StyledBottomGrid>
				<InputWithIcon
					iconSrc={input}
					height={50}
					fontSize={16}
					placeholder="Введите номер"
					textAlign={TextInputAlign.Center}
					value={numberInput}
					onChange={handleNumberInputChange}
					onFocus={() => dispatch(setPlateInputFocus(true))}
					onBlur={() => dispatch(setPlateInputFocus(false))}
					active={!!numberInput}
				/>
				<Button
					color={regionState ? ButtonColors.Select2 : ButtonColors.Default}
					height={ButtonHeight.Height50}
					style={{ padding: '0px 16px', minWidth: 0, maxWidth: '100%' }}
					onClick={() => dispatch(setRegionSelectVisible(true))}
					disabled={isCustomType(plateTypeList[selectedTypeIndex].type)}
				>
					<StyledRegionSelectButtonContent
						$active={!!regionState}
						$disabled={isCustomType(plateTypeList[selectedTypeIndex].type)}
					>
						<img src={flag} width={14} height={14} />
						<Text>
							{(!regionState ||
								isCustomType(plateTypeList[selectedTypeIndex].type)) &&
								'Выбрать регион'}
							{regionState &&
								!isCustomType(plateTypeList[selectedTypeIndex].type) && (
									<Fragment>
										<span className="region-code">
											{regionState.code.padStart(2, '0')}
										</span>{' '}
										<span>({regionState.name})</span>
									</Fragment>
								)}
						</Text>
					</StyledRegionSelectButtonContent>
				</Button>
				<Button
					color={ButtonColors.Select2}
					height={ButtonHeight.Height50}
					onClick={handlePreview}
					style={{ gridColumn: 'span 2' }}
					disabled={tryDisabled}
				>
					<StyledButtonContent>
						{!tryDisabled && <img src={tryIcon} />}
						<Text>
							{tryDisabled ? 'Выберите гос. номер' : 'Примерить номера на ТС'}
						</Text>
					</StyledButtonContent>
				</Button>
			</StyledBottomGrid>
		</StyledContainer>
	);
};

export { ChangeTab };
